/**
 * Portal session for external users who signed in with an e-mail code
 * (no MSAL account). The access token lives in localStorage; the refresh
 * token is an httpOnly cookie scoped to /api/auth/email-code and is rotated
 * by POST /refresh. tokenManager.getToken() returns this token whenever a
 * portal session is active, so the 200+ call sites that pass (instance,
 * accounts) keep working unchanged.
 */
const KEY = 'lgl_portal_session';
const API = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '').replace(/\/api$/, '') + '/api/auth/email-code';

let refreshing = null;

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function write(session) {
  if (session) localStorage.setItem(KEY, JSON.stringify(session));
  else localStorage.removeItem(KEY);
  window.dispatchEvent(new Event('lgl-portal-session'));
}

export function isActive() {
  return !!read()?.token;
}

export function current() {
  return read();
}

export function start({ token, expiresIn, user }) {
  write({ token, exp: Date.now() + (expiresIn || 43200) * 1000, user: user || null });
}

export function clear() {
  write(null);
}

async function post(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch (_) { /* no body */ }
  return { ok: res.ok, status: res.status, data };
}

export async function isEnabled() {
  try {
    const res = await fetch(`${API}/enabled`, { credentials: 'include' });
    const data = await res.json();
    return !!data?.enabled;
  } catch (_) {
    return false;
  }
}

export const requestCode = (email) => post('/request', { email });
export const verifyCode = (email, code) => post('/verify', { email, code });

/** Rotate the refresh cookie and store a new access token. Returns the token or null. */
export async function refresh() {
  if (!refreshing) {
    refreshing = (async () => {
      const { ok, data } = await post('/refresh');
      if (ok && data?.token) {
        start({ token: data.token, expiresIn: data.expiresIn, user: data.user });
        return data.token;
      }
      clear();
      return null;
    })().finally(() => { refreshing = null; });
  }
  return refreshing;
}

/** Access token for API calls; refreshes when inside the last 5 minutes. */
export async function getAccessToken() {
  const s = read();
  if (!s?.token) return null;
  if (s.exp - Date.now() < 5 * 60 * 1000) return refresh();
  return s.token;
}

export async function logout() {
  try { await post('/logout'); } catch (_) { /* best effort */ }
  clear();
}
